import Graphic from "@arcgis/core/Graphic";
import { DrawSymbol } from "./DrawStyles.helper";
import { Polygon, Polyline } from "@arcgis/core/geometry";

type DataType = "overpass-turbo";

export interface OverPassTurbo {
  elements: Array<Element>;
  generator: string;
  osm3s: string;
  version: string;
}

type ElementType = "node" | "way";

interface NodeElement {
  id: number;
  lat: number;
  lon: number;
  type: "node";
  tags: Record<string, any>;
}

interface WayElement {
  id: number;
  type: "way";
  tags: Record<string, any>;
  geometry: Array<{ lat: number; lon: number }>;
  nodes: number[];
}

interface RelationElement {
  id: number;
  type: "relation";
  tags: Record<string, any>;
  geometry: Array<{ lat: number; lon: number }>;
  members: {
    type: "way";
    ref: number;
    role: "inner" | "outer";
    geometry: Array<{ lat: number; lon: number }>;
  }[];
}

type Element = NodeElement | WayElement | RelationElement;

type Type = "overpass-turbo";

export class DataProvider {
  type: Type;
  constructor(type: Type) {
    this.type = type;
  }

  url: RequestInfo | URL = "https://overpass-api.de/api/interpreter";

  async getData(
    query: string,
    signal: AbortSignal | null
  ): Promise<{ items?: OverPassTurbo; error?: string }> {
    return await fetch(`${this.url}?data=${query}`, { signal })
      .then((response) => {
        if (!response.ok) {

          //return { items: undefined, error: response.statusText };
          throw new Error("Network response was not ok " + response.statusText);
        }
        return response.json();
      })
      .then((data: OverPassTurbo | any) => {
        return {
          items: data,
          error: data.remark,
        };
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          return { items: undefined, error: undefined };
        } else {
          return { items: undefined, error: "Incorrect query. Try to fix it." };
        }
      });
  }

  generateTableContent(attributes: any) {
    let tableHTML = "<table style='width:100%; border-collapse: collapse;'>";
    for (const key in attributes) {
      tableHTML += `
        <tr>
          <th style="text-align: left; padding: 5px; border: 1px solid #ddd;">${key}</th>
          <td style="padding: 5px; border: 1px solid #ddd;">${attributes[key]}</td>
        </tr>`;
    }
    tableHTML += "</table>";
    return tableHTML;
  }

  parseDataOverpassTurbo(data?: OverPassTurbo): Graphic[] | undefined {
    if (!data) return;
    return data.elements
      .map((element) => {
        try {
          switch (element.type) {
            case "node":
              const point = {
                type: "point",
                x: element.lon,
                y: element.lat,
                spatialReference: { wkid: 4326 },
              };

              const pointGraphic = new Graphic({
                geometry: point,
                popupTemplate: {
                  title: "Point",
                  content: this.generateTableContent(element.tags),
                },
                attributes: {
                  idnode: element.id,
                  name: "Point",
                  type: "point",
                  ...element.tags,
                },
                symbol: DrawSymbol("point", "sketch"),
              });

              return pointGraphic;
            case "way":
              const vertices = [element.geometry.map((g) => [g.lon, g.lat])];
              if (element.nodes[0] === element.nodes.slice(-1)[0]) {
                const polygon = new Polygon({
                  rings: vertices,
                  spatialReference: { wkid: 4326 },
                });

                const polygonGraphic = new Graphic({
                  geometry: polygon,
                  popupTemplate: {
                    title: "Polygon",
                    content: this.generateTableContent(element.tags),
                  },
                  attributes: {
                    idnode: element.id,
                    name: "Polygon",
                    type: "polygon",
                    ...element.tags,
                  },
                  symbol: DrawSymbol("polygon", "sketch"),
                });

                return polygonGraphic;
              } else {
                const polyline = new Polyline({
                  paths: vertices,
                  spatialReference: { wkid: 4326 },
                });

                const polylineGraphic = new Graphic({
                  geometry: polyline,
                  popupTemplate: {
                    title: "Polyline",
                    content: this.generateTableContent(element.tags),
                  },
                  attributes: {
                    idnode: element.id,
                    name: "Polyline",
                    type: "polyline",
                  },
                  symbol: DrawSymbol("polyline", "sketch"),
                });

                return polylineGraphic;
              }
            case "relation":
              const multi: number[][][] = [];

              //outer ring
              const outerMember = element.members.filter(
                (el) => el.role === "outer"
              );
              const innerMember = element.members.filter(
                (el) => el.role !== "outer"
              );

              if (outerMember && outerMember.length) {
                outerMember.forEach((outM) => {
                  const coordinates = outM.geometry.map((g) => [g.lon, g.lat]);
                  multi.push(coordinates);
                });
              }

              if (innerMember && innerMember.length) {
                innerMember.forEach((outM) => {
                  const coordinates = outM.geometry.map((g) => [g.lon, g.lat]);
                  multi.push(coordinates);
                });
              }

              const multiPolygon = new Polygon({
                rings: multi,
                spatialReference: { wkid: 4326 },
              });

              const multiGraphic = new Graphic({
                geometry: multiPolygon,
                popupTemplate: {
                  title: "Multipolygon",
                  content: this.generateTableContent(element.tags),
                },
                attributes: {
                  idnode: element.id,
                  name: "Polygon",
                  type: "polygon",
                },
                symbol: DrawSymbol("polygon", "sketch"),
              });

              return multiGraphic;

            default:
              break;
          }
        } catch (error) {
          console.log(error);
        }
      })
      .filter((value) => value !== undefined) as Graphic[];
  }
}
